import React, { useEffect, useState } from 'react';
import styles from './jiraSettings.module.css';
import { Button, Col, Form, message, Row, Select, Switch } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { get } from '../../utils/fetch';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          workspace?: OptionType;
          project?: OptionType;
          list?: OptionType;
          new_tasks?: boolean;
          sync_task_updates?: boolean;
          new_comments?: boolean;
          sync_trello_comments?: boolean;
          sync_trello_status?: boolean;
          task_status?: OptionType;
          status_mapping?: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect: () => void;
}

const TrelloSettings: React.FC<Props> = ({
  projectId,
  organizationId,
  integration,
  onDisconnect
}) => {
  const [form] = Form.useForm();

  const {
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Trello',
    onDisconnect
  });

  const [selectedWorkspace, setSelectedWorkspace] = useState<
    OptionType | undefined
  >(integration?.settings?.workspace);
  const [selectedBoard, setSelectedBoard] = useState<OptionType | undefined>(
    integration?.settings?.project
  );
  const [selectedList, setSelectedList] = useState<OptionType | undefined>(
    integration?.settings?.list
  );
  const [selectedStatus, setSelectedStatus] = useState<OptionType | undefined>(
    integration?.settings?.task_status
  );

  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncTrelloComments, setSyncTrelloComments] = useState(
    integration?.settings?.sync_trello_comments ?? false
  );
  const [syncTrelloStatus, setSyncTrelloStatus] = useState(
    integration?.settings?.sync_trello_status ?? false
  );
  const [statusMappings, setStatusMappings] = useState<Record<string, string>>(
    {}
  );

  const [workspaceOptions, setWorkspaceOptions] = useState<OptionType[]>([]);
  const [boardOptions, setBoardOptions] = useState<OptionType[]>([]);
  const [listOptions, setListOptions] = useState<OptionType[]>([]);
  const [bugherdStatusOptions, setBugherdStatusOptions] = useState<
    OptionType[]
  >([]);

  const [isWorkspacesLoading, setIsWorkspacesLoading] = useState(false);
  const [isBoardsLoading, setIsBoardsLoading] = useState(false);
  const [isListsLoading, setIsListsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      const invertedMappings = Object.entries(
        integration.settings.status_mapping
      ).reduce((acc, [trelloListId, bugherdColumnId]) => {
        acc[bugherdColumnId] = trelloListId;
        return acc;
      }, {} as Record<string, string>);
      setStatusMappings(invertedMappings);
    }
  }, [integration]);

  const getWorkspaces = async () => {
    if (!integration?.enabled) return;

    setIsWorkspacesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=workspaces`
      );
      setWorkspaceOptions(results);
    } catch (error) {
      message.error('Failed to fetch Trello workspaces. Please try again.');
    } finally {
      setIsWorkspacesLoading(false);
    }
  };

  const getBoards = async (workspaceId?: string) => {
    if (!integration?.enabled) return;

    setIsBoardsLoading(true);
    try {
      const siteName = workspaceId || 'boards';
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=${siteName}`
      );
      setBoardOptions(results);
    } catch (error) {
      message.error('Failed to fetch Trello boards. Please try again.');
    } finally {
      setIsBoardsLoading(false);
    }
  };

  const getLists = async (boardId?: string) => {
    if (!integration?.enabled || !boardId) {
      setListOptions([]);
      return;
    }

    setIsListsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=lists:${boardId}`
      );
      setListOptions(results);
    } catch (error) {
      message.error('Failed to fetch Trello lists. Please try again.');
    } finally {
      setIsListsLoading(false);
    }
  };

  const getStatusColumns = async (currentProjectId: string) => {
    if (!integration?.enabled) return;

    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${currentProjectId}`);
      const columns: OptionType[] = results.columns.map(
        ({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        })
      );
      setBugherdStatusOptions(columns);
    } catch (error) {
      message.error(
        'Failed to fetch BugHerd status columns. Please try again.'
      );
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleWorkspaceChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedWorkspace(next);
    setSelectedBoard(undefined);
    setSelectedList(undefined);
    setStatusMappings({});
    setListOptions([]);
    form.setFieldsValue({ project: undefined, list: undefined });
    getBoards(next?.value);
  };

  const handleBoardChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedBoard(next);
    setSelectedList(undefined);
    setStatusMappings({});
    form.setFieldsValue({ list: undefined });
    getLists(next?.value);
  };

  const handleListChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedList(next);
  };

  const handleStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedStatus(next);
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    trelloListId: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: trelloListId
    });
  };

  const handleSubmit = () => {
    const validBugherdColumnIds = bugherdStatusOptions.map(
      option => option.value
    );
    const hasLoadedStatuses = validBugherdColumnIds.length > 0;
    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const trelloListId = statusMappings[bugherdColumnId];
        if (!trelloListId) return acc;
        if (
          hasLoadedStatuses &&
          !validBugherdColumnIds.includes(bugherdColumnId)
        )
          return acc;
        acc[trelloListId] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        workspace: selectedWorkspace || {},
        project: selectedBoard,
        list: selectedList,
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_trello_comments: syncTrelloComments,
        sync_trello_status: syncTrelloStatus,
        task_status: selectedStatus,
        status_mapping: cleanedStatusMappings
      },
      { form }
    );
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getWorkspaces();
      getBoards(selectedWorkspace?.value);
      getLists(selectedBoard?.value);
      getStatusColumns(projectId);
    }
  }, [integration, projectId, organizationId]);

  const renderStatusMappings = () => (
    <div className={styles.statusMappings}>
      <Row className={styles.statusMappingRow} gutter={16}>
        <Col span={11}>
          <strong>BugHerd Status</strong>
        </Col>
        <Col span={2}></Col>
        <Col span={11}>
          <strong>Trello List</strong>
        </Col>
      </Row>
      {bugherdStatusOptions.map(bugherdStatus => (
        <Row
          key={bugherdStatus.value}
          className={styles.statusMappingRow}
          gutter={16}
          align="middle"
        >
          <Col span={11}>
            <span className={styles.jiraStatusLabel}>
              {bugherdStatus.label}
            </span>
          </Col>
          <Col span={2} style={{ textAlign: 'center' }}>
            <span>↔</span>
          </Col>
          <Col span={11}>
            <Select
              className={styles.bugherdColumnSelect}
              placeholder="Select Trello list"
              value={statusMappings[bugherdStatus.value]}
              onChange={value =>
                handleStatusMappingChange(bugherdStatus.value, value)
              }
              options={listOptions}
              loading={isListsLoading}
              disabled={!selectedBoard}
              allowClear
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  const canManageUsers =
    !!integration?.id && !!projectIntegrationId && !!selectedBoard?.value;

  const renderIntegrationSettings = () => (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your Trello board</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="workspace"
                label="Trello Workspace"
                className={styles.jiraProject}
                initialValue={selectedWorkspace?.value}
                tooltip="Optional. Leave blank to choose from all open boards you can access."
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="All workspaces"
                  optionFilterProp="label"
                  options={workspaceOptions}
                  onChange={handleWorkspaceChange}
                  loading={isWorkspacesLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="project"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Trello Board
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Trello board.'
                  }
                ]}
                initialValue={selectedBoard?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a board"
                  optionFilterProp="label"
                  options={boardOptions}
                  onChange={handleBoardChange}
                  loading={isBoardsLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="list"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Default Trello List
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Trello list.'
                  }
                ]}
                initialValue={selectedList?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a list"
                  optionFilterProp="label"
                  options={listOptions}
                  onChange={handleListChange}
                  loading={isListsLoading}
                  disabled={!selectedBoard}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to Trello"
                className={styles.autoPost}
              >
                <Switch checked={autoPost} onChange={setAutoPost} />
              </Form.Item>
            </Col>
            {autoPost && (
              <Col span={24}>
                <Form.Item
                  name="statusColumn"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="Optional. Select a BugHerd status to trigger card creation in Trello."
                >
                  <Select
                    placeholder="Select a status"
                    options={bugherdStatusOptions}
                    onChange={handleStatusChange}
                    loading={isStatusLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to Trello">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Trello"
              >
                <Switch checked={newComments} onChange={setNewComments} />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Trello to BugHerd"
              >
                <Switch
                  checked={syncTrelloComments}
                  onChange={setSyncTrelloComments}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync Trello list changes to BugHerd statuses"
              >
                <Switch
                  checked={syncTrelloStatus}
                  onChange={setSyncTrelloStatus}
                  disabled={!selectedBoard}
                />
              </Form.Item>
            </Col>
          </Row>
          {syncTrelloStatus && (
            <div className={styles.jiraEditRow}>{renderStatusMappings()}</div>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Sync users between Trello and BugHerd"
              >
                {canManageUsers ? (
                  <a
                    href={`/settings/organizations/${organizationId}/team?manage_trello_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&board_id=${selectedBoard?.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.manageLink}
                  >
                    Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                  </a>
                ) : (
                  <span>Save a board before managing user mappings.</span>
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Row
          className={styles.jiraEditRow}
          justify="space-between"
          align="middle"
        >
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          {projectIntegrationId && (
            <Col>
              <Button
                type="text"
                shape="round"
                onClick={() => disconnect({ deleteFallback: true })}
                className={styles.disconnectButton}
                danger
              >
                Disconnect
              </Button>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="trello"
          projectId={projectId}
          organizationId={organizationId}
          type="Trello"
          display="Trello"
          provider="trello"
        />
      )}
    </div>
  );
};

export default TrelloSettings;
